export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  roles: UserRole[];
  unit: Pick<Unit, 'id' | 'name'>;
  interviewAssignments?: InterviewAssignment[];
  teamAssignments?: TeamAssignment[];
  createdAt?: string;
  updatedAt?: string;
  isAdmin: boolean;
}

export interface RegistrationState {
  open: boolean
  closesAt: string | null
  cap: number | null
  registeredCount: number
}

export interface Cycle {
  id: string;
  displayName: string;
  monthYear: string;
  status: CycleStatus;
  startedAt?: string;
  endedAt?: string;
  unit: Unit;
  createdAt: string;
  updatedAt: string;
}

export interface Candidate {
  id: string;
  candidateNumber: number;
  teamNumber: number;
  status: CandidateStatus;
  inactivationReason?: string;
  inactivationReasonText?: string;
  lastStatusChangeAt?: string;
  finalCycleScore?: number;
  finalSocioScore?: number;
  teamleadRank?: number;
  redFlag: boolean;
  cycle: Cycle;
  createdAt: string;
  updatedAt: string;
}

export interface Unit {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InterviewAssignment {
  id: string;
  user: User;
  cycle: Cycle;
  candidate: Candidate;
  createdAt: string;
  updatedAt: string;
}

export interface TeamAssignment {
  id: string;
  teamNumber: number;
  role: UserRole;
  user: User;
  cycle: Cycle;
  createdAt: string;
  updatedAt: string;
}

export interface CandidateStatusDto {
  id: string;
  candidateNumber: number;
  teamNumber: number;
  status: CandidateStatus;
  lastStatusChangeAt?: string;
  inactivationReason?: string;
  inactivationReasonText?: string;
  finalCycleScore?: number;
  finalSocioScore?: number;
  finalAssesmentScore?: number;
  finalInterviewScore?: number;
  teamleadRank?: number;
  redFlag: boolean;
  candidatePersonalMatam?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}

export interface TeamStatusDto {
  teamNumber: number;
  teamLeadDisplayName?: string;
  candidates: CandidateStatusDto[];
}

export interface CycleStatusAggregateDto {
  total: number;
  active: number;
  inactive: number;
  gibushCompleted: number;
  gibushIncompleted: number;
  unknown: number;
}

export interface CycleStatusResponseDto {
  cycleId: string;
  monthYear: string;
  displayName: string;
  teams: TeamStatusDto[];
  aggregate?: CycleStatusAggregateDto;
}

export interface MissingCandidateDto {
  candidateId: string;
  candidateNumber: number;
  teamNumber: number;
}

export interface MissingSubmitterDto {
  userId: string;
  userName: string;
  missingCandidates: MissingCandidateDto[];
}

export interface FormTrackerEntryDto {
  cycleFormAssignmentId: string;
  formName: string;
  formType: string;
  roleRelevancy: string[];
  expectedCount: number;
  submittedCount: number;
  missingBySubmitter: MissingSubmitterDto[];
}

export interface SubmissionTrackerResponseDto {
  cycleId: string;
  cycleDisplayName: string;
  forms: FormTrackerEntryDto[];
}

export interface SharedParameter {
  id: string;
  name: string;
  description: string | null;
  maxGrade: number;
}

export interface StationGearItem {
  item: string;
  quantity: number;
}

export interface StationInfoForEvaluator {
  /** The station's purpose, shown to assessors as guidance (P2) */
  description?: string;
  includedParameterIds?: string[];
  notes?: string;
  includedParameters?: string[];
  stationLevel?: string;
  limitation?: string;
  /** P2 station guidance — a fixed set of fields */
  gear?: StationGearItem[];
  timeLimitMinutes?: number;
  space?: string;
  minAssessors?: number;
  safetyNotes?: string;
}

// ── Statements (P2) ──────────────────────────────────────────────────────────

export type StatementTone = 'positive' | 'negative' | 'neutral';

/** A bank statement. `sharedParameterId` links it to one parameter of the unit's bank (P5); admin-only. */
export interface StatementTemplate {
  id: string;
  text: string;
  tone: StatementTone;
  sharedParameterId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CycleStatement {
  assignmentId: string;
  statementId: string;
  /** The bank statement this was copied from; null once it's deleted from the bank. */
  templateId: string | null;
  text: string;
  tone: StatementTone;
  sharedParameterId: string | null;
  /** The linked parameter's current bank name, resolved by the server. */
  parameterName: string | null;
}

export interface Station {
  id: string;
  legacyStationId?: string | null;
  name: string;
  description?: string | null;
  stationType: 'ordered' | 'accumulative' | 'accumulative_2' | 'individual';
  stationLevel: number;
  appliedParameterIds?: string[] | null;
  parameters?: object[] | null;
  accumulativeConfig?: object | null;
  infoForEvaluator?: StationInfoForEvaluator | null;
  includeAssessorFreeScore: boolean;
  isTemplate: boolean;
  cycle?: Cycle | null;
  /** Owning unit; `null` = shared legacy bank row (read + clone only). */
  unit?: { id: string } | null;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  ADMIN = 'admin',
  TEAMLEAD = 'teamlead',
  ASSESSOR = 'assessor',
  SOCIO = 'socio',
  INTERVIEWER = 'interviewer',
  VIEWER = 'viewer'
}

export enum CycleStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  CLOSED = 'closed'
}

export enum CandidateStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  GIBUSH_COMPLETED = 'gibushCompleted',
  GIBUSH_INCOMPLETED = 'gibushIncompleted'
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  user: User;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roles?: UserRole[];
}

export interface CreateCycleDto {
  displayName: string;
  monthYear: string;
  status?: CycleStatus;
  startedAt?: string;
  endedAt?: string;
  forms?: string[];
}

export interface UpdateCandidateStatusDto {
  status: CandidateStatus;
  inactivationReason?: string;
  inactivationReasonText?: string;
  candidatePersonalMatam?: number | null;
}

export interface AssignToTeamDto {
  userId: string;
  teamNumber: number;
  role: UserRole;
}

export interface BulkTeamAssignmentDto {
  assignments: AssignToTeamDto[];
}

export interface CreateInterviewAssignmentDto {
  userId: string;
  candidateId: string;
}

export interface BulkInterviewAssignmentDto {
  assignments: CreateInterviewAssignmentDto[];
}

export interface AssignInterviewerToCandidatesDto {
  userId: string;
  candidateIds: string[];
}

export interface FormDefinition {
  id: string;
  name: string;
  formType: 'generic' | 'socio' | 'teamleadRank' | 'interview';
  roleRelevancy: string[];
  structure?: any;
  weightInFinalScore: number;
  isMandatoryForClosure: boolean;
  isActive: boolean;
  isTemplate: boolean;
  templateId?: string;
  cycle?: Cycle | null;
  createdAt: string;
  updatedAt: string;
}

export interface FormTemplate {
  id: string;
  name: string;
  formType: 'generic' | 'socio' | 'interview';
  roleRelevancy: string[];
  structure?: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CycleFormAssignment {
  id: string;
  formType: string;
  weightInFinalScore: number;
  isMandatoryForClosure: boolean;
  isActive: boolean;
  structureSnapshot?: any;
  template: FormTemplate;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFormTemplateData {
  name: string;
  formType: 'generic' | 'socio' | 'interview';
  roleRelevancy: string[];
  structure?: any;
  isActive?: boolean;
}

export interface CreateCycleFormAssignmentData {
  templateId: string;
  cycleId: string;
  weightInFinalScore?: number;
  isMandatoryForClosure?: boolean;
  isActive?: boolean;
}

export interface FormSubmissionUser {
  id: string;
  firstName: string;
  lastName: string;
}

export interface FormSubmissionRecord {
  id: string;
  submissionData: Record<string, unknown>;
  formDefinitionId: string;
  formType: string;
  weightInFinalScore: number;
  submittedBy: FormSubmissionUser;
}

export interface CandidateDetailRecord extends CandidateStatusDto {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  formSubmissions: FormSubmissionRecord[];
  createdAt: string;
  updatedAt: string;
}

export interface BatchCandidateDetailResponse {
  candidates: CandidateDetailRecord[];
  notFoundIds: string[];
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface CreateFormDefinitionData {
  name: string;
  formType: 'generic' | 'socio' | 'teamleadRank' | 'interview';
  roleRelevancy: string[];
  structure?: any;
  weightInFinalScore?: number;
  isMandatoryForClosure?: boolean;
  isActive?: boolean;
  isTemplate?: boolean;
  cycleId?: string;
  templateId?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/** Body of PUT /cycles/:cycleId/weights; `weights: null` clears an item. */
export interface SaveWeightsBody {
  forms?: { assignmentId: string; weights: Record<string, number> | null }[];
  stations?: { stationId: string; weights: Record<string, number> | null }[];
}

/** Gradebox field inside a form structure; `weight` is the per-cycle share (percent). */
export interface FormGradeField {
  type: string;
  fieldKey: string;
  title: string;
  weight?: number;
}

/** Parameter entry inside a station's `parameters`; `weight` is the per-cycle share (percent). */
export interface StationParam {
  valueType: 'scale' | 'text';
  parameterId: string;
  name: string;
  weight?: number;
}
